import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { IconButton } from "../../../components/ui";

type BottomSheetProps = {
  open: boolean;
  inline?: boolean;
  title: string;
  titleId: string;
  disabled?: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

const CLOSE_DISTANCE = 90;
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Sheets can stack; the page unlocks only when the last one closes.
let scrollLocks = 0;
let restoreScroll = () => {};
function lockScroll() {
  if (scrollLocks++ > 0) return;
  const { body, documentElement: html } = document;
  const y = window.scrollY;
  const before = { htmlOverflow: html.style.overflow, position: body.style.position, top: body.style.top, width: body.style.width };
  // overflow alone doesn't stop iOS scrolling the page behind; fixing the body does.
  html.style.overflow = "hidden";
  body.style.position = "fixed";
  body.style.top = `-${y}px`;
  body.style.width = "100%";
  restoreScroll = () => {
    html.style.overflow = before.htmlOverflow;
    body.style.position = before.position;
    body.style.top = before.top;
    body.style.width = before.width;
    window.scrollTo(0, y);
  };
}
function unlockScroll() {
  if (--scrollLocks === 0) restoreScroll();
}
// Open modal sheets, newest last: Escape and Tab belong to the top one.
const openSheets: object[] = [];

/*
 * A sheet that slides up from the bottom on phones and is a centred modal
 * anywhere wider than a phone held upright (520px: tablets and desktop).
 */

export const BottomSheet = ({
  open,
  inline = false,
  title,
  titleId,
  disabled,
  onClose,
  children,
}: BottomSheetProps) => {
  const panelRef = useRef<HTMLElement | null>(null);
  const gesture = useRef<{
    pointerId: number;
    startY: number;
    moved: boolean;
  } | null>(null);

  const modal = open && !inline;
  const close = useRef(onClose);
  const blocked = useRef(disabled);
  useEffect(() => {
    close.current = onClose;
    blocked.current = disabled;
  });
  // A modal sheet takes focus, keeps Tab inside, closes on Escape, locks the
  // page behind it, and hands focus back to where it was.
  useEffect(() => {
    if (!modal) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const sheet = {};
    openSheets.push(sheet);
    lockScroll();
    panelRef.current?.focus({ preventScroll: true });
    const keys = (event: KeyboardEvent) => {
      if (openSheets.at(-1) !== sheet) return;
      const panel = panelRef.current;
      if (event.key === "Escape") {
        if (!blocked.current) {
          event.preventDefault();
          close.current();
        }
        return;
      }
      if (event.key !== "Tab" || !panel) return;
      const items = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((item) => item.offsetParent !== null);
      if (!items.length) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!panel.contains(active) || (event.shiftKey && (active === first || active === panel))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keys);
    return () => {
      document.removeEventListener("keydown", keys);
      openSheets.splice(openSheets.indexOf(sheet), 1);
      unlockScroll();
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [modal]);

  if (!open) return null;

  if (inline) {
    return (
      <section className="editor-panel" aria-labelledby={titleId}>
        <h2 className="font-lora text-[32px] leading-tight font-semibold lg:px-1.5 lg:text-[26px] lg:leading-snug" id={titleId}>{title}</h2>
        {children}
      </section>
    );
  }

  const settle = () => {
    const panel = panelRef.current;
    if (!panel) return;
    panel.style.transition = "transform 160ms ease";
    panel.style.transform = "";
    window.setTimeout(() => {
      if (panelRef.current) panelRef.current.style.transition = "";
    }, 170);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || event.pointerType === "mouse") return;
    gesture.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const active = gesture.current;
    const panel = panelRef.current;
    if (!active || !panel || event.pointerId !== active.pointerId) return;
    const dy = Math.max(0, event.clientY - active.startY);
    if (dy > 4) active.moved = true;
    if (active.moved) panel.style.transform = `translateY(${dy}px)`;
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    const active = gesture.current;
    gesture.current = null;
    if (!active || event.pointerId !== active.pointerId) return;
    const dy = event.clientY - active.startY;
    settle();
    if (active.moved && dy > CLOSE_DISTANCE && !disabled) onClose();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-hazumu-ink/40 px-3 pb-3 min-[520px]:items-center min-[520px]:px-6 min-[520px]:pb-0">
      <button
        aria-label={`Close ${title}`}
        className="absolute inset-0 cursor-default"
        onClick={() => !disabled && onClose()}
        type="button"
      />
      <section
        aria-labelledby={titleId}
        aria-modal="true"
        className="relative z-10 flex max-h-[80vh] w-full supports-[height:1dvh]:max-h-[80dvh] outline-none max-w-lg flex-col rounded-3xl border border-hazumu-border bg-hazumu-white p-5 shadow-xl shadow-hazumu-ink/20 min-[520px]:p-6"
        ref={panelRef}
        role="dialog"
        tabIndex={-1}
      >
        <div
          className="-mx-5 -mt-5 mb-4 shrink-0 touch-none px-5 pb-1 pt-3 min-[520px]:hidden"
          onPointerCancel={handlePointerEnd}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
        >
          <div className="mx-auto h-1 w-10 rounded-full bg-hazumu-grey" />
        </div>
        <div className="mb-3 flex shrink-0 items-center justify-between">
          <h2 className="font-lora text-xl font-semibold" id={titleId}>
            {title}
          </h2>
          <IconButton aria-label="Close" disabled={disabled} onClick={onClose}>
            <X size={18} />
          </IconButton>
        </div>
        {children}
      </section>
    </div>
  );
};
